<template>
  <v-dialog
    v-model="janelaAberta"
    persistent
    max-width="440"
    content-class="confirmation-window-dialog"
  >
    <v-card
      class="confirmation-window"
      :class="[
        `confirmation-window--${variantVisual}`,
        { 'confirmation-window--destructive': destructive }
      ]"
      elevation="0"
    >
      <div class="confirmation-window__inner">
        <div class="confirmation-window__header">
          <div
            class="confirmation-window__icon-wrapper"
            :class="`confirmation-window__icon--${variantVisual}`"
          >
            <slot name="icon">
              <component
                :is="iconComponent"
                :size="iconSize"
                weight="fill"
              />
            </slot>
          </div>
          <h2 class="confirmation-window__title">{{ title }}</h2>
        </div>
        <div class="confirmation-window__content">
          <div
            class="confirmation-window__message"
            data-test="msg-confirmacao"
            v-html="message"
            :style="messageStyle"
          ></div>
          <slot></slot>
          <template v-if="destructive">
            <p class="confirmation-window__irreversible">
              {{ irreversibleMessage }}
            </p>
            <v-text-field
              v-model="confirmInput"
              class="confirmation-window__confirm-input"
              :placeholder="placeholderDoCampo"
              outlined
              dense
              hide-details
              autocomplete="off"
              data-test="input-confirmar-destrutivo"
              @keyup.enter="submitIfConfirmed"
            />
          </template>
        </div>
      </div>
      <v-card-actions class="mb-1">
        <v-spacer />
        <Button
          v-if="showSecondaryButton"
          variant="tonal"
          class="mr-2"
          tonal-color="black"
          @click="handleSecondaryClick"
          data-test="btn-cancelar"
        >
          {{ textSecondaryButton }}
        </Button>
        <Button
          :variant="primaryButtonVariant"
          :tonal-color="tonalColor"
          class="confirmation-window__btn confirmation-window__btn--primary"
          :disabled="isPrimaryDisabled"
          data-test="btn-confirmar"
          @click="handlePrimaryClick"
        >
          {{ textPrimaryButton }}
        </Button>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { PhCheckCircle, PhXCircle, PhWarning, PhQuestion, PhInfo } from 'phosphor-vue';
import Button from './Button.vue';

export default {
  name: 'ConfirmationWindow',
  components: {
    PhCheckCircle,
    PhXCircle,
    PhWarning,
    PhQuestion,
    PhInfo,
    Button
  },
  model: {
    prop: 'openWindow',
    event: 'onChange'
  },

  props: {
    message: { type: String, default: 'Confirma a alteração?' },
    title: { type: String, default: 'Confirmação' },
    variant: {
      type: String,
      default: 'confirmation',
      validator: (v) => ['success', 'error', 'warning', 'information', 'confirmation'].includes(v)
    },
    textPrimaryButton: { type: String, default: 'Sim' },
    textSecondaryButton: { type: String, default: 'Não' },
    messageStyle: { type: [String, Object, Array], default: null },
    showSecondaryButton: { type: Boolean, default: true },
    openWindow: { type: Boolean, default: false },
    callbackPrimary: { type: Function, default: () => {} },
    callbackSecondary: { type: Function, default: null },

    destructive: { type: Boolean, default: false },

    irreversibleMessage: {
      type: String,
      default: 'Esta ação não poderá ser desfeita.'
    },

    /* Sem valor proprio o placeholder e montado a partir da palavra. Fixar o
       texto aqui fazia quem passasse `confirm-keyword` proprio continuar lendo
       "Confirmo" no campo — instrucao errada, e o botao nunca liberava. */
    confirmInputPlaceholder: { type: String, default: null },

    confirmKeyword: { type: String, default: 'CONFIRMAR' }
  },

  data() {
    return {
      janelaAberta: this.openWindow,
      confirmInput: ''
    };
  },

  computed: {
    iconSize() {
      return 32;
    },
    /* Destrutivo manda na cor. A acao e irreversivel seja qual for o `variant`
       que a tela passou — e varias passam "warning" —, entao o halo do icone e
       o aviso saem em vermelho junto com o botao. Sem isto o mesmo modal dava
       dois sinais: halo ambar e botao vermelho. */
    variantVisual() {
      return this.destructive ? 'error' : this.variant;
    },
    placeholderDoCampo() {
      return this.confirmInputPlaceholder || `Digite "${this.confirmKeyword}" para continuar`;
    },
    iconComponent() {
      const iconMap = {
        success: PhCheckCircle,
        error: PhXCircle,
        warning: PhWarning,
        confirmation: PhQuestion,
        information: PhInfo
      };

      return iconMap[this.variantVisual] || PhInfo;
    },
    primaryButtonVariant() {
      return this.destructive ? 'destructive' : 'tonal';
    },
    isPrimaryDisabled() {
      return this.destructive && !this.canConfirm;
    },
    canConfirm() {
      if (!this.destructive) return true;

      return this.confirmInput.trim().toLowerCase() === this.confirmKeyword.toLowerCase();
    },
    tonalColor() {
      const colorMap = {
        success: 'success',
        error: 'error',
        warning: 'warning',
        information: 'primary',
        confirmation: 'primary'
      };

      return colorMap[this.variant] || 'primary';
    }
  },

  methods: {
    closeWindow() {
      this.janelaAberta = false;
      this.confirmInput = '';
      this.$emit('onChange', this.janelaAberta);
    },
    handleSecondaryClick() {
      if (typeof this.callbackSecondary === 'function') {
        this.callbackSecondary();
      }

      this.closeWindow();
    },
    handlePrimaryClick() {
      if (this.destructive && !this.canConfirm) return;

      this.callbackPrimary();
      this.closeWindow();
    },
    submitIfConfirmed() {
      if (this.canConfirm) {
        this.handlePrimaryClick();
      }
    }
  },

  watch: {
    openWindow(valor) {
      this.janelaAberta = !!valor;

      if (valor) {
        this.confirmInput = '';
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.confirmation-window-dialog {
  border-radius: var(--tc-radius-sm);
  overflow: hidden;
  box-shadow: none;
}

.confirmation-window {
  border-radius: var(--tc-radius-sm);
  font-family: var(--tc-font-body);
  overflow: hidden;
  box-shadow: none;
}

.confirmation-window__inner {
  display: flex;
  flex-direction: column;
  padding: 20px 20px 0;
}

.confirmation-window__header {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;
  width: 100%;
}

.confirmation-window__icon-wrapper {
  flex-shrink: 0;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.confirmation-window__icon-wrapper ::v-deep(svg) {
  width: 32px;
  height: 32px;
  flex-shrink: 0;
}

.confirmation-window__icon-wrapper.confirmation-window__icon--success {
  color: var(--v-success-base);
  background-color: color-mix(in srgb, var(--v-success-base) 14%, transparent);
}

.confirmation-window__icon-wrapper.confirmation-window__icon--error {
  color: var(--v-error-base);
  background-color: color-mix(in srgb, var(--v-error-base) 14%, transparent);
}

.confirmation-window__icon-wrapper.confirmation-window__icon--warning {
  color: var(--v-warningText-base);
  background-color: color-mix(in srgb, var(--v-warningText-base) 18%, transparent);
}

.confirmation-window__icon-wrapper.confirmation-window__icon--information,
.confirmation-window__icon-wrapper.confirmation-window__icon--confirmation {
  color: var(--v-primary-base);
  background-color: color-mix(in srgb, var(--v-primary-base) 14%, transparent);
}

.confirmation-window__content {
  width: 100%;
  text-align: left;
  padding-left: 0;
}

.confirmation-window__irreversible {
  font-size: 13px;
  margin: 12px 0 8px 0;
  font-weight: 500;
}

/* Mesmo vermelho do botao primario destrutivo, para o modal falar uma coisa so. */
.confirmation-window--destructive .confirmation-window__irreversible {
  color: var(--v-error-base);
}

.confirmation-window__confirm-input {
  margin-top: 4px;
}

.confirmation-window__confirm-input ::v-deep(.v-input__slot) {
  font-size: 14px;
}

.confirmation-window__title {
  font-weight: var(--tc-fw-semibold);
  font-size: 20px;
  line-height: 1.3;
  margin: 0;
}

.confirmation-window--success .confirmation-window__title {
  color: var(--v-success-base);
}

.confirmation-window--error .confirmation-window__title {
  color: var(--v-error-base);
}

.confirmation-window--warning .confirmation-window__title {
  color: var(--v-warningText-base);
}

.confirmation-window--information .confirmation-window__title,
.confirmation-window--confirmation .confirmation-window__title {
  color: var(--v-primary-base);
}

.confirmation-window__message {
  font-size: 14px;
  line-height: 1.5;
  word-break: normal;
  text-align: left;
}

.confirmation-window__message ::v-deep a {
  color: var(--v-primary-base);
}

/* Mensagens mais longas podem vir com paragrafos e lista */
.confirmation-window__message ::v-deep p {
  margin: 0 0 8px;
}

.confirmation-window__message ::v-deep p:last-child {
  margin-bottom: 0;
}

.confirmation-window__message ::v-deep ul {
  margin: 0;
  padding-left: 18px;
}

.confirmation-window__message ::v-deep li + li {
  margin-top: 4px;
}
</style>
